import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Absolute imports (from 'pages/...')
import TelegramNotificationManager from 'pages/admin/TelegramNotificationManager';
import AdminSalesLadder from 'pages/admin/AdminSalesLadder';
import AdminCommissionManagement from 'pages/admin/AdminCommissionManagement';
import AgentDashboard from 'pages/agent/AgentDashboard';
import BecomeAgentPage from 'pages/BecomeAgentPage';
import SalesLadderPage from 'pages/SalesLadderPage';
import BulkOrderPage from 'pages/bulk-orders/BulkOrderPage';
import BulkOrderDetailPage from 'pages/bulk-orders/BulkOrderDetailPage';
import BulkOrderHistoryPage from 'pages/bulk-orders/BulkOrderHistoryPage';
import NotFoundPage from 'pages/error/NotFoundPage';
import ServerErrorPage from 'pages/error/ServerErrorPage';

// Relative imports (from './...')
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SocketProvider } from './contexts/SocketContext';
import AppearanceProvider from './contexts/AppearanceProvider';
import Layout from './components/layout/Layout';
import ShopLayout from './components/layout/ShopLayout';
import LoadingScreen from './components/common/LoadingScreen';
import ErrorBoundary from './components/common/ErrorBoundary';
import DocumentHead from './components/layout/DocumentHead';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import './styles/toast.css';

// Lazy-loaded components (must be after all other imports)
import LoginPage from 'pages/auth/LoginPage';
const RegisterPage = lazy(() => import('pages/auth/RegisterPage'));
const AgentPaymentPage = lazy(() => import('pages/auth/AgentPaymentPage'));
const AgentPaymentVerifyPage = lazy(() => import('pages/auth/AgentPaymentVerifyPage'));
const ForgotPasswordPage = lazy(() => import('pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('pages/auth/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('pages/auth/AuthCallbackPage'));
const VerifyPhonePage = lazy(() => import('./pages/auth/VerifyPhonePage'));
const TermsPage = lazy(() => import('pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('pages/legal/PrivacyPage'));
const DashboardPage = lazy(() => import('pages/dashboard/DashboardPage').then((m: any) => ({ default: m.default })));
const PackagesPage = lazy(() => import('pages/packages/PackagesPage'));
const PackageDetailPage = lazy(() => import('pages/packages/PackageDetailPage'));
const OrdersPage = lazy(() => import('pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('pages/orders/OrderDetailPage'));
const OrderTrackingPage = lazy(() => import('pages/orders/OrderTrackingPage'));
const WalletPage = lazy(() => import('pages/wallet/WalletPage'));
const PaymentManagementPage = lazy(() => import('pages/payments/PaymentManagementPage'));
const ProfilePage = lazy(() => import('pages/profile/ProfilePage'));
const BeneficiariesPage = lazy(() => import('pages/profile/BeneficiariesPage'));
const SettingsPage = lazy(() => import('pages/settings/SettingsPage'));
const PaymentPage = lazy(() => import('pages/checkout/PaymentPage'));
const PaymentStatusPage = lazy(() => import('pages/checkout/PaymentStatusPage'));
const PaymentSuccessPage = lazy(() => import('pages/checkout/PaymentSuccessPage'));
const AdminDashboard = lazy(() => import('pages/admin/AdminDashboard'));
const UserManagementPage = lazy(() => import('pages/admin/UserManagementPage'));
const UserExportPage = lazy(() => import('pages/admin/UserExportPage'));
const AdminPackages = lazy(() => import('pages/admin/AdminPackages'));
const OrderManagementPage = lazy(() => import('pages/admin/OrderManagementPage'));
const AirtimeOrderManagementPage = lazy(() => import('pages/admin/AirtimeOrderManagementPage'));
const AdminBulkOrdersPage = lazy(() => import('pages/admin/AdminOrders'));
const AdminPayments = lazy(() => import('pages/admin/AdminPayments'));
const AdminSettingsPage = lazy(() => import('pages/admin/AdminSettingsPage'));
const AgentPaymentConfigPage = lazy(() => import('pages/admin/AgentPaymentConfigPage'));
const ManualWalletTopupPage = lazy(() => import('pages/admin/ManualWalletTopupPage'));
const WalletLeaderboardPage = lazy(() => import('pages/admin/WalletLeaderboardPage'));
const WaecStockManagementPage = lazy(() => import('pages/admin/WaecStockManagementPage'));
const AdminMTNAFAPage = lazy(() => import('pages/admin/AdminMTNAFAPage'));
const PricingSettingsPage = lazy(() => import('pages/admin/PricingSettingsPage'));
const AnnouncementManagementPage = lazy(() => import('pages/admin/AnnouncementManagementPage'));
const DeliveryStatusPage = lazy(() => import('pages/admin/DeliveryStatusPage'));
const PaymentVerificationPage = lazy(() => import('pages/admin/PaymentVerificationPage'));
const AdminWithdrawalsPage = lazy(() => import('pages/admin/AdminWithdrawalsPage'));
const AdminPayoutHistoryPage = lazy(() => import('pages/admin/AdminPayoutHistoryPage'));
const AdminFraudEvents = lazy(() => import('pages/admin/AdminFraudEvents'));
const AdminSupplierRoutingPage = lazy(() => import('pages/admin/AdminSupplierRoutingPage'));
const AdminBackfillDiagnosticsPage = lazy(() => import('pages/admin/AdminBackfillDiagnosticsPage'));
const AdminDiscountsPage = lazy(() => import('pages/admin/AdminDiscountsPage'));

// Feature Pages
const AirtimePage = lazy(() => import('pages/airtime/AirtimePage'));
const WAECPage = lazy(() => import('pages/waec/WAECPage'));
const WaecHistoryPage = lazy(() => import('pages/waec/WaecHistoryPage'));
const MTNAFAPage = lazy(() => import('pages/mtn-afa/MTNAFAPage'));
const ReferralsPage = lazy(() => import('pages/referrals/ReferralsPage'));
const GroupsPage = lazy(() => import('pages/groups/GroupsPage'));
const AutoRenewPage = lazy(() => import('pages/orders/AutoRenewPage'));
const BusinessDashboardPage = lazy(() => import('pages/business/BusinessDashboardPage'));
const BecomeBusinessPage = lazy(() => import('pages/business/BecomeBusinessPage'));
const SendDataPage = lazy(() => import('pages/diaspora/SendDataPage'));
const SendDataSuccessPage = lazy(() => import('pages/diaspora/SendDataSuccessPage'));
const CommissionsPage = lazy(() => import('pages/commissions/CommissionsPage'));
const WithdrawalsPage = lazy(() => import('pages/withdrawals/WithdrawalsPage'));
const CustomerManagementPage = lazy(() => import('pages/customers/CustomerManagementPage'));
const StorefrontPage = lazy(() => import('pages/storefront/StorefrontPage'));
const PublicStorefrontPage = lazy(() => import('pages/storefront/PublicStorefrontPage'));
const WatchlistPage = lazy(() => import('pages/watchlist/WatchlistPage'));

const SuspenseFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingScreen />
  </div>
);

const RouteWrapper = ({ element: Element }: { element: React.ReactNode }) => (
  <ErrorBoundary>
    <Suspense fallback={<SuspenseFallback />}>
      {Element}
    </Suspense>
  </ErrorBoundary>
);

const ReferralParamCapture = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = (params.get('ref') || '').trim();
    if (!ref) return;

    const normalized = ref.toUpperCase();
    if (!/^REF[A-Z0-9]+$/.test(normalized)) return;

    try {
      localStorage.setItem('purchaseReferralCode', normalized);
      localStorage.setItem('purchaseReferralCodeAt', String(Date.now()));
    } catch {
    }
  }, [location.search]);

  return null;
};

const RootRedirect: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return <Navigate to={isAuthenticated ? '/dashboard' : '/packages'} replace />;
};

const AppRoutes = () => {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Root: guests → /packages, authenticated → /dashboard */}
      <Route path="/" element={<RootRedirect />} />

      {/* Public Routes */}
      <Route path="/login" element={<RouteWrapper element={<LoginPage />} />} />
      <Route path="/register" element={<RouteWrapper element={<RegisterPage />} />} />
      <Route path="/agent-payment" element={<RouteWrapper element={<AgentPaymentPage />} />} />
      <Route path="/agent-payment/verify" element={<RouteWrapper element={<AgentPaymentVerifyPage />} />} />
      <Route path="/forgot-password" element={<RouteWrapper element={<ForgotPasswordPage />} />} />
      <Route path="/reset-password" element={<RouteWrapper element={<ResetPasswordPage />} />} />
      <Route path="/auth/callback" element={<RouteWrapper element={<AuthCallbackPage />} />} />
      <Route path="/verify-phone" element={<RouteWrapper element={<VerifyPhonePage />} />} />
      <Route path="/become-agent" element={<RouteWrapper element={<BecomeAgentPage />} />} />
      <Route path="/terms" element={<RouteWrapper element={<TermsPage />} />} />
      <Route path="/privacy" element={<RouteWrapper element={<PrivacyPage />} />} />
      <Route path="/send-data" element={<RouteWrapper element={<SendDataPage />} />} />
      <Route path="/send-data/success" element={<RouteWrapper element={<SendDataSuccessPage />} />} />
      <Route path="/:username" element={<RouteWrapper element={<PublicStorefrontPage />} />} />

      {/* Shop browse routes — public, no auth required */}
      <Route element={<ShopLayout />}>
        <Route path="/packages" element={<RouteWrapper element={<PackagesPage />} />} />
        <Route path="/packages/:network/:id" element={<RouteWrapper element={<PackageDetailPage />} />} />
      </Route>

      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ErrorBoundary>
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          </ErrorBoundary>
        }
      >
        <Route path="dashboard" element={<RouteWrapper element={<DashboardPage />} />} />
        <Route path="orders" element={<RouteWrapper element={<OrdersPage />} />} />
        <Route path="orders/:id" element={<RouteWrapper element={<OrderDetailPage />} />} />
        <Route path="orders/:id/track" element={<RouteWrapper element={<OrderTrackingPage />} />} />
        <Route path="auto-renew" element={<RouteWrapper element={<AutoRenewPage />} />} />
        <Route path="watchlist" element={<RouteWrapper element={<WatchlistPage />} />} />
        <Route path="business" element={<RouteWrapper element={<BusinessDashboardPage />} />} />
        <Route path="become-business" element={<RouteWrapper element={<BecomeBusinessPage />} />} />
        <Route path="bulk-orders" element={<RouteWrapper element={<BulkOrderPage />} />} />
        <Route path="bulk-orders/:id" element={<RouteWrapper element={<BulkOrderDetailPage />} />} />
        <Route path="bulk-orders/history" element={<RouteWrapper element={<BulkOrderHistoryPage />} />} />
        <Route path="wallet" element={<RouteWrapper element={<WalletPage />} />} />
        <Route path="payments" element={<RouteWrapper element={<PaymentManagementPage />} />} />
        <Route path="profile" element={<RouteWrapper element={<ProfilePage />} />} />
        <Route path="beneficiaries" element={<RouteWrapper element={<BeneficiariesPage />} />} />
        <Route path="groups" element={<RouteWrapper element={<GroupsPage />} />} />
        <Route path="settings" element={<RouteWrapper element={<SettingsPage />} />} />
        <Route
          path="loyalty-points"
          element={
            <RouteWrapper
              element={
                <ProtectedRoute requiredRole="user">
                  <SalesLadderPage />
                </ProtectedRoute>
              }
            />
          }
        />
        <Route path="sales-ladder" element={<Navigate to="/loyalty-points" replace />} />
        
        {/* Feature Routes */}
        <Route path="airtime" element={<RouteWrapper element={<AirtimePage />} />} />
        <Route path="waec" element={<RouteWrapper element={<WAECPage />} />} />
        <Route path="waec/history" element={<RouteWrapper element={<WaecHistoryPage />} />} />
        <Route path="checker-history" element={<Navigate to="/waec/history" replace />} />
        <Route path="mtn-afa" element={<RouteWrapper element={<MTNAFAPage />} />} />
        <Route
          path="referrals"
          element={
            <RouteWrapper
              element={
                <ProtectedRoute requiredRole="user">
                  <ReferralsPage />
                </ProtectedRoute>
              }
            />
          }
        />
        <Route
          path="commissions"
          element={
            <RouteWrapper
              element={
                <ProtectedRoute requiredRole="agent">
                  <CommissionsPage />
                </ProtectedRoute>
              }
            />
          }
        />
        <Route path="withdrawals" element={<RouteWrapper element={<WithdrawalsPage />} />} />
        <Route path="customers" element={
          <RouteWrapper element={
            <ProtectedRoute requiredRole="agent">
              <CustomerManagementPage />
            </ProtectedRoute>
          } />
        } />
        <Route path="storefront" element={
          <RouteWrapper element={
            <ProtectedRoute requiredRole="agent">
              <StorefrontPage />
            </ProtectedRoute>
          } />
        } />
        
        {/* Checkout Routes */}
        <Route path="checkout" element={<RouteWrapper element={<PaymentPage />} />} />
        <Route path="checkout/payment" element={<RouteWrapper element={<PaymentPage />} />} />
        <Route path="checkout/status" element={<RouteWrapper element={<PaymentStatusPage />} />} />
        <Route path="checkout/success" element={<RouteWrapper element={<PaymentSuccessPage />} />} />
        
        {/* Admin Routes */}
        <Route path="admin" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/users" element={
          <RouteWrapper element={
            <AdminRoute>
              <UserManagementPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/users/export" element={
          <RouteWrapper element={
            <AdminRoute>
              <UserExportPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/packages" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminPackages />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/orders" element={
          <RouteWrapper element={
            <AdminRoute>
              <OrderManagementPage />
            </AdminRoute>
          } />
        } />

        <Route path="admin/airtime-orders" element={
          <RouteWrapper element={
            <AdminRoute>
              <AirtimeOrderManagementPage />
            </AdminRoute>
          } />
        } />

        <Route path="admin/bulk-orders" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminBulkOrdersPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/payments" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminPayments />
            </AdminRoute>
          } />
        } />

        <Route path="admin/withdrawals" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminWithdrawalsPage />
            </AdminRoute>
          } />
        } />

        <Route path="admin/discounts" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminDiscountsPage />
            </AdminRoute>
          } />
        } />

        <Route path="admin/payout-history" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminPayoutHistoryPage />
            </AdminRoute>
          } />
        } />

        <Route path="admin/fraud-events" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminFraudEvents />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/analytics" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
        } />

        <Route path="admin/routing" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminSupplierRoutingPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/settings" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminSettingsPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/config" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminSettingsPage />
            </AdminRoute>
          } />
        } />
        <Route path="admin/agent-payment" element={
          <RouteWrapper element={
            <AdminRoute>
              <AgentPaymentConfigPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/wallet-topup" element={
          <RouteWrapper element={
            <AdminRoute>
              <ManualWalletTopupPage />
            </AdminRoute>
          } />
        } />

        <Route path="admin/wallet-leaderboard" element={
          <RouteWrapper element={
            <AdminRoute>
              <WalletLeaderboardPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/waec-stock" element={
          <RouteWrapper element={
            <AdminRoute>
              <WaecStockManagementPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/mtn-afa" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminMTNAFAPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/pricing" element={
          <RouteWrapper element={
            <AdminRoute>
              <PricingSettingsPage />
            </AdminRoute>
          } />
        } />
        
                <Route path="admin/telegram" element={
          <RouteWrapper element={
            <AdminRoute>
              <TelegramNotificationManager />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/sales-ladder" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminSalesLadder />
            </AdminRoute>
          } />
        } />

        <Route path="admin/commissions" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminCommissionManagement />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/announcements" element={
          <RouteWrapper element={
            <AdminRoute>
              <AnnouncementManagementPage />
            </AdminRoute>
          } />
        } />
        <Route path="admin/delivery-status" element={
          <RouteWrapper element={
            <AdminRoute>
              <DeliveryStatusPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/payment-verification" element={
          <RouteWrapper element={
            <AdminRoute>
              <PaymentVerificationPage />
            </AdminRoute>
          } />
        } />
        
        <Route path="admin/backfill-diagnostics" element={
          <RouteWrapper element={
            <AdminRoute>
              <AdminBackfillDiagnosticsPage />
            </AdminRoute>
          } />
        } />

        {/* Agent Routes */}
        <Route path="agent" element={
          <RouteWrapper element={
            <ProtectedRoute requiredRole="agent">
              <AgentDashboard />
            </ProtectedRoute>
          } />
        } />
      </Route>
      
      {/* Error Routes */}
      <Route path="/500" element={<RouteWrapper element={<ServerErrorPage />} />} />
      <Route path="*" element={<RouteWrapper element={<NotFoundPage />} />} />
    </Routes>
  );
}

function App() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Initialize app
    
    setIsReady(true);
  }, []);

  if (!isReady) {
    return <LoadingScreen />;
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider>
        <AuthProvider>
          <AppearanceProvider>
            <SocketProvider>
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <ReferralParamCapture />
                <DocumentHead />
                <AppRoutes />
                
                {/* Global toast notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    // Default styles that adapt to theme
                    style: {
                      background: 'rgba(255, 255, 255, 0.95)',
                      color: '#1f2937',
                      border: '1px solid rgba(229, 231, 235, 0.8)',
                      borderRadius: '0.75rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      padding: '12px 16px',
                      boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15)',
                      backdropFilter: 'blur(8px)',
                    },
                    success: {
                      iconTheme: {
                        primary: '#10b981',
                        secondary: '#ffffff',
                      },
                      style: {
                        background: 'rgba(240, 253, 244, 0.95)',
                        color: '#14532d',
                        border: '1px solid rgba(187, 247, 208, 0.8)',
                        backdropFilter: 'blur(8px)',
                      },
                      className: 'toast-success',
                    },
                    error: {
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#ffffff',
                      },
                      style: {
                        background: 'rgba(254, 242, 242, 0.95)',
                        color: '#7f1d1d',
                        border: '1px solid rgba(254, 202, 202, 0.8)',
                        backdropFilter: 'blur(8px)',
                      },
                      className: 'toast-error',
                    },
                    loading: {
                      iconTheme: {
                        primary: '#3b82f6',
                        secondary: '#ffffff',
                      },
                      style: {
                        background: 'rgba(239, 246, 255, 0.95)',
                        color: '#1e3a8a',
                        border: '1px solid rgba(191, 219, 254, 0.8)',
                        backdropFilter: 'blur(8px)',
                      },
                      className: 'toast-loading',
                    },
                    className: 'toast',
                  }}
                />
              </div>
            </SocketProvider>
          </AppearanceProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
